<template>
    <div>
        <b-modal title="Production Documents"
                 size="xl"
                 centered
                 no-close-on-backdrop
                 v-model="dialogOpen"
                 @hidden="onDialogHidden">
            <b-alert v-model="showErrors" variant="danger" dismissible>
                <div v-for="(error, index) in currentErrors" :key="index">
                    <p>{{ error }}</p>
                </div>
            </b-alert>

            <ul class="list-group list-group-flush" v-if="!productionDocuments || !productionDocuments.length">
                <li class="list-group-item">
                    The list is currently empty
                </li>
            </ul>
            <b-table id="productionDocumentsTable" striped hover :items="productionDocuments" :fields="tableFields" :current-page="currentPage" :per-page="perPage" v-if="productionDocuments && productionDocuments.length">
                <template v-slot:cell(status)="row">
                    {{ productionStatusText(row.item) }}
                </template>
                <template v-slot:cell(actions)="row">
                    <b-button-group>
                        <b-button size="sm"
                                  variant="outline-secondary"
                                  key="viewdocument"
                                  title="Download"
                                  :href="documentDownloadUrl(row.item)"
                                  v-if="legacyDocumentDownloadAvailable(row.item)">
                            <i class="fa fa-download"></i>
                        </b-button>
                        <b-dropdown v-else-if="newDocumentDownloadAvailable(row.item)"
                                    size="sm"
                                    variant="outline-secondary"
                                    split
                                    right
                                    :split-href="documentDownloadUrl(defaultDocument(row.item.productionFiles))"
                                    title="Download"
                                    html="<i class='fas fa-download mr-1'></i>">
                            <b-dropdown-item v-for="file in row.item.productionFiles"
                                             v-bind:key="file.documentType"
                                             :href="documentDownloadUrl(file)">
                                {{documentTypeDisplayName(file)}}
                            </b-dropdown-item>
                        </b-dropdown>
                    </b-button-group>
                </template>
            </b-table>
            <b-pagination v-model="currentPage"
                          :total-rows="productionDocuments.length"
                          :per-page="perPage"
                          size="sm"
                          align="right"
                          aria-controls="productionDocumentsTable"
                          v-if="(productionDocuments != null) && (productionDocuments.length > 0)">
            </b-pagination>

            <template v-slot:modal-footer="{ cancel }">
                <b-button @click="cancel">Close</b-button>
                <b-button variant="primary" @click="openProductionDocumentDetailsDialog" v-if="!readonly">Request</b-button>
            </template>
        </b-modal>

        <b-modal title="Document Details"
                 size="xl"
                 centered
                 no-close-on-backdrop
                 v-model="productionDocumentDetailsDialogOpen"
                 @ok.prevent="requestProductionDocument">
            <b-form-row class="mt-3">
                <b-form-group label="First Page Number"
                              label-for="firstPageNumber"
                              class="col-md-2">
                    <b-form-input name="firstPageNumber"
                                  v-model="productionDocumentDetails.additionalProperties.FirstPageNumber"
                                  type="number"
                                  min="1"
                                  autofocus>
                    </b-form-input>
                </b-form-group>
                <b-form-group label="Portion/Extract"
                              label-for="portion"
                              class="col-md-5">
                    <b-form-select name="portion" v-model="productionDocumentDetails.portion" :options="portionOptions">
                    </b-form-select>
                </b-form-group>
            </b-form-row>

            <b-form-group label="Court Name"
                          label-for="courtNameLines">
                <b-form-textarea name="courtNameLines"
                                 v-model="productionDocumentDetails.additionalProperties.CourtNameLines"
                                 rows="3"></b-form-textarea>
            </b-form-group>

            <b-form-group label="Matter/s"
                          label-for="matters">
                <b-button text="Add party/counsel" variant="info" size="sm" class="mb-2" @click="addMatter">
                    Add matter
                </b-button>
                <b-list-group small flush v-if="matters && matters.length">
                    <b-list-group-item size="sm" class="w-100 m-0 p-0">
                        <b-row class="m-0 p-0 small">
                            <b-col cols="4" lg="1"><label class="mb-0">Index</label></b-col>
                            <b-col cols="4" lg="1"><label class="mb-0">Year</label></b-col>
                            <b-col cols="4" lg="1"><label class="mb-0">Type</label></b-col>
                            <b-col><label class="mb-0">Name</label></b-col>
                        </b-row>
                    </b-list-group-item>
                    <draggable v-model="matters" group="matters">

                        <b-list-group-item size="sm"
                                           v-for="(matter,index) in matters"
                                           :key="'matter_' + index"
                                           class="w-100 m-0 p-0">

                            <b-row class="m-0 p-0 align-items-center">
                                <b-col cols="4" lg="1" class="p-1"><b-input type="number" v-model="matter.Number" size="sm" :min="0" :max="999999"></b-input></b-col>
                                <b-col cols="4" lg="1" class="p-1"><b-input type="number" v-model="matter.Year" size="sm" :min="1900" :max="2999"></b-input></b-col>
                                <b-col cols="4" lg="1" class="p-1"><b-input v-model="matter.MatterType" size="sm"></b-input></b-col>
                                <b-col class="p-1 d-flex align-items-center">
                                    <b-input v-model="matter.Name" size="sm" class="flex-fill"></b-input>
                                    <b-button @click="removeMatter(index)" size="sm" variant="outline-danger" title="Remove" class="ml-1" v-if="index > 0"><i class="fas fa-times"></i></b-button>
                                    <i class="fas fa-grip-lines ml-1"></i>
                                </b-col>
                            </b-row>
                        </b-list-group-item>

                    </draggable>

                </b-list-group>

            </b-form-group>

            <b-form-group label="Document Title"
                          label-for="documentTitleLines">
                <b-form-textarea name="documentTitleLines"
                                 v-model="productionDocumentDetails.additionalProperties.DocumentTitleLines"
                                 rows="3"></b-form-textarea>
            </b-form-group>

            <b-form-group label="Judge/s / Jury"
                          label-for="judgeJuryLines">
                <b-form-textarea name="judgeJuryLines"
                                 v-model="productionDocumentDetails.additionalProperties.JudgeJuryLines"
                                 rows="2"></b-form-textarea>
            </b-form-group>

            <b-form-row class="mt-3">
                <b-form-group label="Judge Header Text"
                              label-for="judgeHeaderText"
                              class="col-md-4">
                    <select2 name="judgeHeaderText"
                             v-model="productionDocumentDetails.additionalProperties.JudgeHeaderText"
                             :options="judgeHeaderTextOptions"
                             tags
                             allow-clear
                             in-b-modal></select2>
                </b-form-group>
                <b-form-group label="Judge Body Text"
                              label-for="judgeBodyText"
                              class="col-md-4">
                    <select2 name="judgeBodyText"
                             v-model="productionDocumentDetails.additionalProperties.JudgeBodyText"
                             :options="judgeBodyTextOptions"
                             tags
                             allow-clear
                             in-b-modal></select2>
                </b-form-group>
                <b-form-group label="Judge Footer Text"
                              label-for="judgeFooterText"
                              class="col-md-4">
                    <select2 name="judgeFooterText"
                             v-model="productionDocumentDetails.additionalProperties.JudgeFooterText"
                             :options="judgeFooterTextOptions"
                             tags
                             allow-clear
                             in-b-modal></select2>
                </b-form-group>
            </b-form-row>

            <b-form-group label="Transcript Details"
                          label-for="transcriptDetailsLines">
                <b-form-textarea name="transcriptDetailsLines"
                                 v-model="productionDocumentDetails.additionalProperties.TranscriptDetailsLines"
                                 rows="3"></b-form-textarea>
            </b-form-group>

            <b-form-group label="Parties/Counsel"
                          label-for="partiesAndCounsel">

                <b-dropdown text="Add party/counsel" variant="info" size="sm" class="mb-2">
                    <b-dropdown-item @click="addPartyOrCounsel(partyCounselOption)"
                                     v-for="(partyCounselOption,index) in partyCounselOptions" :key="index">
                        {{ partyCounselOption.DisplayName }}
                    </b-dropdown-item>
                </b-dropdown>

                <b-list-group small flush v-if="partiesAndCounsel && partiesAndCounsel.length">

                    <draggable v-model="partiesAndCounsel" group="partiesAndCounsel">

                        <b-list-group-item size="sm"
                                           v-for="(partyOrCounsel,index) in partiesAndCounsel"
                                           :key="'party_counsel_' + index"
                                           class="w-100 m-0 p-0">

                            <b-row class="m-0 p-0 align-items-center">
                                <template v-if="partyOrCounsel.AdditionalText">
                                    <b-col cols="12" lg="4" xl="5" class="p-1">
                                        <b-input v-model="partyOrCounsel.CounselName" size="sm" placeholder="name required"></b-input>
                                    </b-col>
                                    <b-col class="p-1 pl-2 d-flex align-items-center">
                                        <span class="flex-fill">{{ partyOrCounsel.AdditionalText }}</span>
                                        <b-button @click="removePartyOrCounsel(index)" size="sm" variant="outline-danger" title="Remove"><i class="fas fa-times"></i></b-button>
                                        <i class="fas fa-grip-lines ml-1"></i>
                                    </b-col>
                                </template>
                                <template v-else>
                                    <b-col class="p-1 d-flex align-items-center">
                                        <b-input v-model="partyOrCounsel.MarkupText" size="sm" placeholder="text required" class="flex-fill"></b-input>
                                        <b-button @click="removePartyOrCounsel(index)" size="sm" variant="outline-danger" title="Remove" class="ml-1"><i class="fas fa-times"></i></b-button>
                                        <i class="fas fa-grip-lines ml-1"></i>
                                    </b-col>
                                </template>
                            </b-row>
                        </b-list-group-item>

                    </draggable>

                </b-list-group>

            </b-form-group>

        </b-modal>
    </div>
</template>

<script>
    //import $ from 'jquery';
    import Vue from 'vue';
    import BootstrapVue from 'bootstrap-vue';
    import axios from 'axios';
    import Select2 from '../Select2';
    import draggable from "vuedraggable";
    import ErrorsMixin from '../../mixins/ErrorsMixin';
    import DateTimeMixin from '../../mixins/DateTimeMixin';

    Vue.use(BootstrapVue);

    export default {
        name: 'TopscribeProductionDocumentsDialog',
        mixins: [ErrorsMixin, DateTimeMixin],
        components: {
            'select2': Select2,
            draggable
        },
        model: {
            event: 'change',
            prop: 'active'
        },
        props: {
            apiControllerUrl: {
                type: String,
                required: true
            },
            topscribeDocumentId: {
                type: String,
                required: true
            },
            fileDownloadUrl: {
                type: String,
                default: null
            },
            readonly: {
                type: Boolean,
                default: false
            },
            active: {
                type: Boolean,
                default: false
            }
        },
        data: function () {
            return {
                productionDocuments: null,
                dialogOpen: this.active,
                currentErrors: null,
                showErrors: false,
                tableFields: [
                    {
                        key: 'turnNumbers',
                        label: 'Turns',
                        class: 'fit'
                    },
                    {
                        key: 'portionDisplayName',
                        label: 'Portion',
                        class: 'fit'
                    },
                    {
                        key: 'requestedDateTimeUTC',
                        label: 'Requested',
                        formatter: value => {
                            return this.formatDateTime(value, 'DD/MM/YYYY hh:mma', false);
                        },
                        class: 'fit'
                    },
                    {
                        key: 'requestedByUserDisplayName',
                        label: 'Requested By',
                        class: 'fit'
                    },
                    {
                        key: 'productionStartedDateTimeUTC',
                        label: 'Started',
                        formatter: value => {
                            return this.formatDateTime(value, 'DD/MM hh:mma', false);
                        },
                        class: 'fit'
                    },
                    {
                        key: 'productionFinishedDateTimeUTC',
                        label: 'Completed',
                        formatter: value => {
                            return this.formatDateTime(value, 'DD/MM hh:mma', false);
                        },
                        class: 'fit'
                    },
                    {
                        key: 'status',
                        label: 'Status'
                    },
                    {
                        key: 'actions',
                        label: 'Actions',
                        class: 'fit'
                    }
                ],
                currentPage: 1,
                perPage: 10,

                productionDocumentDetailsDialogOpen: false,
                productionDocumentDetails: {
                    firstTurnNumber: null,
                    lastTurnNumber: null,
                    portion: 'MainTranscript',
                    additionalProperties: { //IMPORTANT! everything in the additionalProperties starts with an upper case letter as this is a dyamic/ExpandoObject in C# and that is converted differently
                        FirstPageNumber: 1,
                        CourtNameLines: null,
                        Matters: [],
                        DocumentTitleLines: null,
                        JudgeJuryLines: null,
                        JudgeHeaderText: null,
                        JudgeBodyText: null,
                        JudgeFooterText: null,
                        TranscriptDetailsLines: null,
                        PartiesAndCounsel: []
                    }
                },
                matters: [],
                partiesAndCounsel: [],
            }
        },
        computed: {
            portionOptions: function () {
                if (this.productionDocumentDetails && this.productionDocumentDetails.additionalProperties && this.productionDocumentDetails.additionalProperties.PortionOptions) {
                    return this.productionDocumentDetails.additionalProperties.PortionOptions;
                }
                return null;
            },
            judgeHeaderTextOptions: function () {
                if (this.productionDocumentDetails && this.productionDocumentDetails.additionalProperties && this.productionDocumentDetails.additionalProperties.JudgeHeaderTextOptions) {
                    return this.productionDocumentDetails.additionalProperties.JudgeHeaderTextOptions;
                }
                return null;
            },
            judgeBodyTextOptions: function () {
                if (this.productionDocumentDetails && this.productionDocumentDetails.additionalProperties && this.productionDocumentDetails.additionalProperties.JudgeBodyTextOptions) {
                    return this.productionDocumentDetails.additionalProperties.JudgeBodyTextOptions;
                }
                return null;
            },
            judgeFooterTextOptions: function () {
                if (this.productionDocumentDetails && this.productionDocumentDetails.additionalProperties && this.productionDocumentDetails.additionalProperties.JudgeFooterTextOptions) {
                    return this.productionDocumentDetails.additionalProperties.JudgeFooterTextOptions;
                }
                return null;
            },
            partyCounselOptions: function () {
                if (this.productionDocumentDetails && this.productionDocumentDetails.additionalProperties && this.productionDocumentDetails.additionalProperties.PartyCounselOptions) {
                    return this.productionDocumentDetails.additionalProperties.PartyCounselOptions;
                }
                return null;
            }
        },
        watch: {
            active: function () {
                if (this.active) {
                    this.reload();
                }
                this.dialogOpen = this.active;
            },
            currentErrors: function () {
                this.showErrors = (this.currentErrors != null) && (this.currentErrors.length > 0);
            }
        },
        methods: {
            reload: function () {
                var url = this.apiControllerUrl;
                if (!url.endsWith('/')) url += '/';
                url += this.topscribeDocumentId;
                url += '/all';
                return axios
                    .get(url)
                    .then(response => {
                        this.productionDocuments = response.data;
                    })
                    .catch(ex => {
                        var currentErrors = this.handleAxiosException(ex);
                        if (currentErrors.length != 0) {
                            this.showErrorsToast(currentErrors);
                        }
                    });
            },
            productionStatusText: function (topscribeProductionDocument) {
                if (topscribeProductionDocument.productionFinishedDateTimeUTC) {
                    if (topscribeProductionDocument.success) return "Succeeded";
                    return "Failed";
                }
                if (topscribeProductionDocument.productionStartedDateTimeUTC) return "In Progress";
                return "Pending";
            },
            legacyDocumentDownloadAvailable: function (topscribeProductionDocument) {
                if (!this.fileDownloadUrl)
                    return false;
                return (topscribeProductionDocument.productionFileId);
            },
            documentDownloadUrl: function (topscribeProductionDocument) {
                if (!this.fileDownloadUrl)
                    return null;
                if (this.fileDownloadUrl.indexOf('00000000-0000-0000-0000-000000000000') >= 0)
                    return this.fileDownloadUrl.replace('00000000-0000-0000-0000-000000000000', topscribeProductionDocument.productionFileId)
                var url = this.fileDownloadUrl;
                if (!url.endsWith('/')) url += '/';
                url += topscribeProductionDocument.productionFileId;
                return url;
            },
            newDocumentDownloadAvailable: function (topscribeProductionDocument) {
                if (!this.fileDownloadUrl)
                    return false;
                return (topscribeProductionDocument.productionFiles && (topscribeProductionDocument.productionFiles.length > 0));
            },
            documentTypeDisplayName: function (document) {
                switch (document.documentType) {
                    case 1:
                        return "Word Document";
                    case 2:
                        return "PDF Document";
                    case 3:
                        return "Text Document";
                    case 4:
                        return "XML Document";
                    default:
                        return "Other Format";
                }
            },
            defaultDocument: function (documents) {
                var defaultItem = null;
                documents.forEach((document) => {
                    if (defaultItem == null || defaultItem.documentType > document.documentType)
                        defaultItem = document;
                });
                return defaultItem;
            },
            openProductionDocumentDetailsDialog: function () {
                var url = this.apiControllerUrl;
                if (!url.endsWith('/')) url += '/';
                url += this.topscribeDocumentId;
                url += '/documentdetails';
                var currentErrors = [];
                axios
                    .get(url)
                    .then(response => {
                        console.debug(JSON.stringify(response.data));
                        this.productionDocumentDetails = response.data;
                        this.productionDocumentDetails.portion = 'MainTranscript';
                        if (this.productionDocumentDetails.additionalProperties.CourtNameLines != null) this.productionDocumentDetails.additionalProperties.CourtNameLines = this.productionDocumentDetails.additionalProperties.CourtNameLines.join("\n");
                        if (this.productionDocumentDetails.additionalProperties.Matters == null) this.matters = [];
                        else this.matters = this.productionDocumentDetails.additionalProperties.Matters;
                        if (this.productionDocumentDetails.additionalProperties.DocumentTitleLines != null) this.productionDocumentDetails.additionalProperties.DocumentTitleLines = this.productionDocumentDetails.additionalProperties.DocumentTitleLines.join("\n");
                        if (this.productionDocumentDetails.additionalProperties.JudgeJuryLines != null) this.productionDocumentDetails.additionalProperties.JudgeJuryLines = this.productionDocumentDetails.additionalProperties.JudgeJuryLines.join("\n");
                        if ((this.productionDocumentDetails.additionalProperties.JudgeHeaderText != null) && (this.productionDocumentDetails.additionalProperties.JudgeHeaderText.length > 0)) {
                            if (!this.productionDocumentDetails.additionalProperties.JudgeHeaderTextOptions) {
                                this.productionDocumentDetails.additionalProperties.JudgeHeaderTextOptions = [];
                            }
                            if (!this.productionDocumentDetails.additionalProperties.JudgeHeaderTextOptions.includes(this.productionDocumentDetails.additionalProperties.JudgeHeaderText)) {
                                this.productionDocumentDetails.additionalProperties.JudgeHeaderTextOptions.push(this.productionDocumentDetails.additionalProperties.JudgeHeaderText);
                            }
                        }
                        if ((this.productionDocumentDetails.additionalProperties.JudgeBodyText != null) && (this.productionDocumentDetails.additionalProperties.JudgeBodyText.length > 0)) {
                            if (!this.productionDocumentDetails.additionalProperties.JudgeBodyTextOptions) {
                                this.productionDocumentDetails.additionalProperties.JudgeBodyTextOptions = [];
                            }
                            if (!this.productionDocumentDetails.additionalProperties.JudgeBodyTextOptions.includes(this.productionDocumentDetails.additionalProperties.JudgeBodyText)) {
                                this.productionDocumentDetails.additionalProperties.JudgeBodyTextOptions.push(this.productionDocumentDetails.additionalProperties.JudgeBodyText);
                            }
                        }
                        if ((this.productionDocumentDetails.additionalProperties.JudgeFooterText != null) && (this.productionDocumentDetails.additionalProperties.JudgeFooterText.length > 0)) {
                            if (!this.productionDocumentDetails.additionalProperties.JudgeFooterTextOptions) {
                                this.productionDocumentDetails.additionalProperties.JudgeFooterTextOptions = [];
                            }
                            if (!this.productionDocumentDetails.additionalProperties.JudgeFooterTextOptions.includes(this.productionDocumentDetails.additionalProperties.JudgeFooterText)) {
                                this.productionDocumentDetails.additionalProperties.JudgeFooterTextOptions.push(this.productionDocumentDetails.additionalProperties.JudgeFooterText);
                            }
                        }
                        if (this.productionDocumentDetails.additionalProperties.TranscriptDetailsLines != null) this.productionDocumentDetails.additionalProperties.TranscriptDetailsLines = this.productionDocumentDetails.additionalProperties.TranscriptDetailsLines.join("\n");
                        if (this.productionDocumentDetails.additionalProperties.PartiesAndCounsel == null) this.partiesAndCounsel = [];
                        else {
                            this.partiesAndCounsel = this.productionDocumentDetails.additionalProperties.PartiesAndCounsel.map(pac => {
                                if (!pac.PartyType) pac.PartyType = pac.DisplayName;
                                if (!pac.CounselName && pac.Name) {
                                    pac.CounselName = pac.Name;
                                    delete pac.Name;
                                }
                                return pac;
                            });
                        }
                    })
                    .catch(ex => {
                        currentErrors = this.handleAxiosException(ex);
                    })
                    .finally(() => {
                        if (currentErrors.length != 0) {
                            this.showErrorsToast(currentErrors);
                        } else {
                            this.productionDocumentDetailsDialogOpen = true;
                        }
                    });
            },
            addMatter: function () {
                if (this.matters == null) this.matters = [];
                this.matters.push({ Number: 0, Year: new Date().getFullYear(), MatterType: null, Name: null });
            },
            removeMatter: function (index) {
                this.matters.splice(index, 1);
            },
            addPartyOrCounsel: function (partyCounselOption) {
                if (this.partiesAndCounsel == null) this.partiesAndCounsel = [];
                this.partiesAndCounsel.push({ ...partyCounselOption });
            },
            removePartyOrCounsel: function (index) {
                this.partiesAndCounsel.splice(index, 1);
            },
            requestProductionDocument: function () {

                var url = this.apiControllerUrl;
                if (!url.endsWith('/')) url += '/';
                url += this.topscribeDocumentId;
                url += '/new';

                var validationErrors = false;
                var i;
                // check if all matter details were provided
                if (this.matters) {
                    for (i = 0; i < this.matters.length; i++) {
                        if ((this.matters[i].Number <= 0) || (this.matters[i].Number > 999999) ||
                            (this.matters[i].Year <= 1900) || (this.matters[i].Year > 2999) ||
                            (this.matters[i].MatterType == null) ||
                            (this.matters[i].MatterType.length == 0) ||
                            (this.matters[i].Name == null) ||
                            (this.matters[i].Name.length == 0)) {
                            this.$bvToast.toast('Please provide an index between [1 and 999999], a year between [1900 and 2999], a type and a name for every matter.', {
                                title: 'Matters',
                                variant: 'danger',
                                appendToast: true
                            });
                            validationErrors = true;
                            break;
                        }
                    }
                }
                // check if all party and counsel names were provided
                if (this.partiesAndCounsel) {
                    for (i = 0; i < this.partiesAndCounsel.length; i++) {
                        var nameOrTextMissing = false;
                        if (this.partiesAndCounsel[i].AdditionalText) {
                            if ((this.partiesAndCounsel[i].CounselName == null) ||
                                (this.partiesAndCounsel[i].CounselName.length == 0)) {
                                nameOrTextMissing = true;
                            }
                        } else {
                            if ((this.partiesAndCounsel[i].MarkupText == null) ||
                                (this.partiesAndCounsel[i].MarkupText.length == 0)) {
                                nameOrTextMissing = true;
                            }
                        }
                        if (nameOrTextMissing) {
                            this.$bvToast.toast('Please provide a name or text for every party/counsel.', {
                                title: 'Parties and Counsel',
                                variant: 'danger',
                                appendToast: true
                            });
                            validationErrors = true;
                            break;
                        }
                    }
                }
                if (validationErrors) return;

                var data = {
                    firstTurnNumber: this.productionDocumentDetails.firstTurnNumber ? parseInt(this.productionDocumentDetails.firstTurnNumber) : null,
                    lastTurnNumber: this.productionDocumentDetails.lastTurnNumber ? parseInt(this.productionDocumentDetails.lastTurnNumber) : null,
                    portion: this.productionDocumentDetails.portion,
                    additionalProperties: {
                        FirstPageNumber: this.productionDocumentDetails.additionalProperties.FirstPageNumber ? parseInt(this.productionDocumentDetails.additionalProperties.FirstPageNumber) : null,
                        CourtNameLines: this.productionDocumentDetails.additionalProperties.CourtNameLines ? this.productionDocumentDetails.additionalProperties.CourtNameLines.split("\n") : null,
                        Matters: this.matters && this.matters.length ? this.matters : null,
                        DocumentTitleLines: this.productionDocumentDetails.additionalProperties.DocumentTitleLines ? this.productionDocumentDetails.additionalProperties.DocumentTitleLines.split("\n") : null,
                        JudgeJuryLines: this.productionDocumentDetails.additionalProperties.JudgeJuryLines ? this.productionDocumentDetails.additionalProperties.JudgeJuryLines.split("\n") : null,
                        JudgeHeaderText: this.productionDocumentDetails.additionalProperties.JudgeHeaderText,
                        JudgeBodyText: this.productionDocumentDetails.additionalProperties.JudgeBodyText,
                        JudgeFooterText: this.productionDocumentDetails.additionalProperties.JudgeFooterText,
                        TranscriptDetailsLines: this.productionDocumentDetails.additionalProperties.TranscriptDetailsLines ? this.productionDocumentDetails.additionalProperties.TranscriptDetailsLines.split("\n") : null,
                        PartiesAndCounsel: this.partiesAndCounsel && this.partiesAndCounsel.length ? this.partiesAndCounsel : null
                    }
                }
                axios
                    .post(url, JSON.stringify(data), {
                        headers: {
                            'Content-Type': 'application/json'
                        }
                    })
                    .then(() => {
                        this.$bvToast.toast('Production Document requested successfully', {
                            title: 'Production Document Requested',
                            variant: 'success',
                            appendToast: true
                        });
                        this.productionDocumentDetailsDialogOpen = false;
                        this.reload();
                    })
                    .catch(ex => {
                        var currentErrors = this.handleAxiosException(ex);
                        if (currentErrors.length != 0) {
                            this.showErrorsToast(currentErrors);
                        }
                    });
            },
            onDialogHidden: function () {
                this.$emit('change', false);
            }
        }
    }
</script>

